"use strict";
/**
 * Admin roles and capabilities, shared by client and server.
 *
 * The capability constants are the full vocabulary of permissions — every one
 * is a named constant so no code references one as a literal string. The
 * `Capability` union is the set a procedure is allowed to require. Add a new
 * capability by adding a constant here (with a one-line note on what it grants)
 * and a union member.
 *
 * `ROLES` lists each role's capabilities explicitly; `Owner` and `Developer`
 * hold every capability via `allCapabilities`, and `roleHasCapability` resolves
 * a caller's roles against it. `ROLES` is also the accepted role vocabulary:
 * `isKnownRole` checks a name against it, and `admins.update` refuses any name
 * it doesn't recognise. The client gates screens and affordances on
 * capabilities and adds React hooks (`client/util/admin.ts`) and translated
 * labels (`client/feature/feature-roles.ts`); the server gates oRPC procedures
 * via `.meta({ access: 'admin', capability })` and resolves them in
 * `userHasCapabilityAsync`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ROLES = exports.CHANNEL_READ_SPACE_CHANNEL_CAPABILITY = exports.CHANNEL_LIST_CAPABILITY = exports.CHANNEL_LIST_ADMIN_CHANNELS_CAPABILITY = exports.CHANNEL_ACCESS_ALL_CHANNELS_CAPABILITY = exports.CHANNEL_MANAGE_CAPABILITY = exports.CHANNEL_VIEW_CAPABILITY = exports.MODERATE_CAPABILITY = exports.REMOVE_COMMENT_CAPABILITY = exports.MODERATION_EDIT_BLOCKLIST_CAPABILITY = exports.MODERATION_VIEW_BLOCKLIST_CAPABILITY = exports.MODERATION_JUDGE_CAPABILITY = exports.MODERATION_ADD_NOTES_CAPABILITY = exports.MODDASHBOARD_CAPABILITY = exports.DEVELOPER_ADD_COMMENTS_CAPABILITY = exports.VIDEOVOTINGLIST_CAPABILITY = exports.STEWARD_COMMENTS_CAPABILITY = exports.FEATURES_TOGGLE_STABLE_CAPABILITY = exports.FEATURES_TOGGLE_EXPERIMENTAL_CAPABILITY = exports.SETTINGS_CAPABILITY = exports.RANKING_EVAL_CAPABILITY = exports.EMAIL_PREVIEW_CAPABILITY = exports.EDITORIALDASH_CAPABILITY = exports.DEVELOPER_TOOLS_CAPABILITY = exports.LIST_ADMINS_CAPABILITY = exports.MANAGE_PROFILE_DETAILS_CAPABILITY = exports.ADMIN_USERS_CAPABILITY = exports.ADMIN_DASHBOARD_CAPABILITY = void 0;
exports.isKnownRole = isKnownRole;
exports.roleHasCapability = roleHasCapability;
exports.rolesGrantingCapability = rolesGrantingCapability;
// Admin surfaces
/** Access the admin dashboard. */
exports.ADMIN_DASHBOARD_CAPABILITY = 'admin/dashboard';
/** Add or remove admins and change their roles. */
exports.ADMIN_USERS_CAPABILITY = 'adminusers/modify-admins';
/**
 * Read the Staff Profiles table and edit what an admin shows publicly — their
 * name override, photo override, and titled-writer title. Deliberately
 * separate from `ADMIN_USERS_CAPABILITY`, which stays the authority to change
 * who is an admin and what they may do: editing a colleague's title is
 * editorial work, and pinning it to the role that grants and revokes access
 * made one person per silo the bottleneck for it.
 *
 * Carries the staff-table read (`admins.getTable`) because that is the table
 * the editing screen is reached through. Beyond the email and roles every
 * admin role already reads via `LIST_ADMINS_CAPABILITY`, it adds only a
 * profile preview and a post count.
 */
exports.MANAGE_PROFILE_DETAILS_CAPABILITY = 'profiles/manage-details';
/**
 * Read the silo's admin roster. Provisionally granted to every admin role
 * because this lean list backs several unrelated admin screens — the role
 * editor and the channel member-picker among them — so no narrower capability
 * is the right gate.
 */
exports.LIST_ADMINS_CAPABILITY = 'admin/list-admins';
/** Access developer tools. */
exports.DEVELOPER_TOOLS_CAPABILITY = 'admin/developer-tools';
/** Access the editorial dashboard (create and manage spaces and stories). */
exports.EDITORIALDASH_CAPABILITY = 'admin/editorialdash';
/** Preview outgoing email templates. */
exports.EMAIL_PREVIEW_CAPABILITY = 'admin/email-preview';
/** Access the ranking-evaluation tools. */
exports.RANKING_EVAL_CAPABILITY = 'admin/ranking-eval';
/** View and edit silo settings. */
exports.SETTINGS_CAPABILITY = 'admin/settings';
// Feature flags
/** Toggle experimental (unstable) features on instances. */
exports.FEATURES_TOGGLE_EXPERIMENTAL_CAPABILITY = 'features/toggle-experimental';
/** Toggle stable features on instances. */
exports.FEATURES_TOGGLE_STABLE_CAPABILITY = 'features/toggle-stable';
// Spaces
/**
 * Steward comments in a space: pin and unpin comments in the feed, and set or
 * clear the space's featured comment. Granted to every admin role today;
 * declared as one capability so what stewards can do stays visible in the role
 * model rather than being an implicit any-admin action.
 */
exports.STEWARD_COMMENTS_CAPABILITY = 'spaces/steward-comments';
// Content
/** Manage video-voting lists. */
exports.VIDEOVOTINGLIST_CAPABILITY = 'content/video-votings';
/** Add comments as a developer (e.g. seed or test comments). */
exports.DEVELOPER_ADD_COMMENTS_CAPABILITY = 'developer/add-comments';
// Moderation
/** View the moderation dashboard. */
exports.MODDASHBOARD_CAPABILITY = 'moddashboard/view';
/** Author internal notes on a moderation task. */
exports.MODERATION_ADD_NOTES_CAPABILITY = 'moderation/add-notes';
/** Make moderation decisions (judge flagged content). */
exports.MODERATION_JUDGE_CAPABILITY = 'moderation/judge';
/** View the moderation blocklist. */
exports.MODERATION_VIEW_BLOCKLIST_CAPABILITY = 'moderation/view-blocklist';
/** Edit the moderation blocklist. */
exports.MODERATION_EDIT_BLOCKLIST_CAPABILITY = 'moderation/edit-blocklist';
/** Remove a comment. */
exports.REMOVE_COMMENT_CAPABILITY = 'remove-comment';
/** Perform moderation actions. */
exports.MODERATE_CAPABILITY = 'moderate';
// Channels
/** View channels. */
exports.CHANNEL_VIEW_CAPABILITY = 'channel/view';
/** Create, edit, and delete channels, and manage their membership. */
exports.CHANNEL_MANAGE_CAPABILITY = 'channel/manage';
/** Access every channel, regardless of membership. */
exports.CHANNEL_ACCESS_ALL_CHANNELS_CAPABILITY = 'channel/access-all-channels';
/**
 * Read one's own channel memberships. Granted to every admin role — even those
 * without `channel/view` — because it is self-scoped (the handler only ever
 * returns the caller's own memberships), so a limited editorial user still
 * needs it to know which channels they belong to.
 */
exports.CHANNEL_LIST_ADMIN_CHANNELS_CAPABILITY = 'channel/list-admin-channels';
/**
 * List the silo's channels — every channel's display info (name, photo, default
 * flag) and its member emails. Granted to every role that reaches the editorial
 * dashboard or the moderation dashboard, because both render channel pickers.
 * Distinct from `channel/view`, which gates the channel-management screen. The
 * member emails it carries are staff emails already visible to these roles
 * through `admins.list`, so this exposes nothing new.
 */
exports.CHANNEL_LIST_CAPABILITY = 'channel/list';
/**
 * Read which channel a space is assigned to. Granted to every role that can
 * reach the editorial dashboard or the moderation dashboard, because both show
 * a space's channel: the editorial channel picker and the moderation reply
 * flow. Not sensitive — it only exposes the assignment, so it needs no
 * `channel/view` membership check.
 */
exports.CHANNEL_READ_SPACE_CHANNEL_CAPABILITY = 'channel/read-space-channel';
exports.ROLES = {
    Owner: { allCapabilities: true },
    Developer: { allCapabilities: true },
    'Super-Moderator': {
        can: [
            exports.REMOVE_COMMENT_CAPABILITY,
            exports.MODERATE_CAPABILITY,
            exports.ADMIN_DASHBOARD_CAPABILITY,
            exports.LIST_ADMINS_CAPABILITY,
            exports.STEWARD_COMMENTS_CAPABILITY,
            exports.MODDASHBOARD_CAPABILITY,
            exports.MODERATION_ADD_NOTES_CAPABILITY,
            exports.MODERATION_JUDGE_CAPABILITY,
            exports.MODERATION_VIEW_BLOCKLIST_CAPABILITY,
            exports.CHANNEL_VIEW_CAPABILITY,
            exports.CHANNEL_LIST_ADMIN_CHANNELS_CAPABILITY,
            exports.CHANNEL_LIST_CAPABILITY,
            exports.CHANNEL_READ_SPACE_CHANNEL_CAPABILITY,
            exports.MODERATION_EDIT_BLOCKLIST_CAPABILITY,
            exports.CHANNEL_ACCESS_ALL_CHANNELS_CAPABILITY,
            exports.EMAIL_PREVIEW_CAPABILITY,
            exports.SETTINGS_CAPABILITY,
        ],
    },
    Moderator: {
        can: [
            exports.REMOVE_COMMENT_CAPABILITY,
            exports.MODERATE_CAPABILITY,
            exports.ADMIN_DASHBOARD_CAPABILITY,
            exports.LIST_ADMINS_CAPABILITY,
            exports.STEWARD_COMMENTS_CAPABILITY,
            exports.MODDASHBOARD_CAPABILITY,
            exports.MODERATION_ADD_NOTES_CAPABILITY,
            exports.MODERATION_JUDGE_CAPABILITY,
            exports.MODERATION_VIEW_BLOCKLIST_CAPABILITY,
            exports.CHANNEL_VIEW_CAPABILITY,
            exports.CHANNEL_LIST_ADMIN_CHANNELS_CAPABILITY,
            exports.CHANNEL_LIST_CAPABILITY,
            exports.CHANNEL_READ_SPACE_CHANNEL_CAPABILITY,
        ],
    },
    Editorial: {
        can: [
            exports.ADMIN_DASHBOARD_CAPABILITY,
            exports.LIST_ADMINS_CAPABILITY,
            exports.MANAGE_PROFILE_DETAILS_CAPABILITY,
            exports.STEWARD_COMMENTS_CAPABILITY,
            exports.FEATURES_TOGGLE_STABLE_CAPABILITY,
            exports.VIDEOVOTINGLIST_CAPABILITY,
            exports.EDITORIALDASH_CAPABILITY,
            exports.SETTINGS_CAPABILITY,
            exports.EMAIL_PREVIEW_CAPABILITY,
            exports.CHANNEL_LIST_ADMIN_CHANNELS_CAPABILITY,
            exports.CHANNEL_LIST_CAPABILITY,
            exports.CHANNEL_READ_SPACE_CHANNEL_CAPABILITY,
        ],
    },
    Analyst: {
        can: [
            exports.ADMIN_DASHBOARD_CAPABILITY,
            exports.LIST_ADMINS_CAPABILITY,
            exports.STEWARD_COMMENTS_CAPABILITY,
            exports.EDITORIALDASH_CAPABILITY,
            exports.MODDASHBOARD_CAPABILITY,
            exports.MODERATION_ADD_NOTES_CAPABILITY,
            exports.MODERATION_JUDGE_CAPABILITY,
            exports.MODERATION_VIEW_BLOCKLIST_CAPABILITY,
            exports.CHANNEL_LIST_ADMIN_CHANNELS_CAPABILITY,
            exports.CHANNEL_LIST_CAPABILITY,
            exports.CHANNEL_READ_SPACE_CHANNEL_CAPABILITY,
        ],
    },
};
/**
 * Whether a role name is one the platform defines. The role vocabulary is
 * closed: `ROLES` is the whole of it, and a name outside it grants nothing.
 * Role writes check against this, so the accepted vocabulary follows `ROLES`
 * instead of being pinned a second time somewhere else.
 */
function isKnownRole(role) {
    return Object.prototype.hasOwnProperty.call(exports.ROLES, role);
}
function roleHasCapability(myRoles, capability) {
    if (!myRoles || !capability) {
        return false;
    }
    for (const role of myRoles) {
        const definition = exports.ROLES[role];
        if (definition?.allCapabilities) {
            return true;
        }
        if (definition?.can?.includes(capability)) {
            return true;
        }
    }
    return false;
}
/**
 * Names of the roles that grant a capability, in `ROLES` declaration order —
 * the inverse of `roleHasCapability`.
 */
function rolesGrantingCapability(capability) {
    if (!capability) {
        return [];
    }
    return Object.entries(exports.ROLES)
        .filter(([, definition]) => definition.allCapabilities || definition.can?.includes(capability))
        .map(([role]) => role);
}
//# sourceMappingURL=roles-capabilities.js.map